import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Edit, Trash2, FileText, Download, Filter, IndianRupee, Eye, Send, MessageCircle, Receipt } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import ViewInvoice from '../components/ViewInvoice';
import EditInvoice from '../components/EditInvoice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);  // Loading state for skeleton
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSendingAll, setIsSendingAll] = useState(false);
  const [showGenerateInvoices, setShowGenerateInvoices] = useState(false);
  const [isGeneratingInvoices, setIsGeneratingInvoices] = useState(false);
  const [backfillMissing, setBackfillMissing] = useState(false);
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [exportEndDate, setExportEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [invoiceSummary, setInvoiceSummary] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Dashboard filter context from navigation
  const dashboardContext = location.state || {};
  const [filterPeriod, setFilterPeriod] = useState(dashboardContext.period || 'overall');
  const [selectedMonth, setSelectedMonth] = useState(dashboardContext.month ? 
    { month: dashboardContext.month, year: dashboardContext.year } : 
    { month: new Date().getMonth() + 1, year: new Date().getFullYear() }
  );
  
  useEffect(() => {
    fetchInvoices();
  }, []);

  // Fetch summary when dates change
  useEffect(() => {
    fetchInvoiceSummary();
  }, [exportStartDate, exportEndDate]);

  // Refetch invoices when filter period or month changes
  useEffect(() => {
    fetchInvoices();
  }, [filterPeriod, selectedMonth]);

  const sendAllUnpaidInvoices = async () => {
    const unpaidInvoices = invoices.filter(inv => inv.status === 'sent' || inv.status === 'overdue');
    
    if (unpaidInvoices.length === 0) {
      alert('No unpaid invoices to send');
      return;
    }

    if (!confirm(`Send ${unpaidInvoices.length} unpaid invoices via WhatsApp? This will take approximately ${Math.ceil(unpaidInvoices.length * 35 / 60)} minutes with smart delays.`)) {
      return;
    }

    setIsSendingAll(true);
    try {
      const messages = unpaidInvoices.map(invoice => ({
        phone_number: invoice.client?.mobile || invoice.client_mobile || '',
        message: `INVOICE REMINDER

Invoice #: ${invoice.invoice_number}
Due Date: ${new Date(invoice.due_date).toLocaleDateString()}
${invoice.client?.name || invoice.client_name || 'Client'}
${invoice.client?.organization_name || invoice.organization_name || ''}

Amount Due: Rs.${invoice.total_amount}
${invoice.status === 'overdue' ? 'This invoice is OVERDUE' : ''}

Contact us for payment assistance
View full invoice: ${window.location.origin}

GSTAssist - Professional GST Management`
      })).filter(m => m.phone_number);

      const response = await axios.post(`${API}/whatsapp/web/send-bulk`, {
        messages: messages
      });

      if (response.data.success) {
        alert(`${unpaidInvoices.length} invoice reminders queued for delivery! ${response.data.estimated_time}`);
      }
    } catch (error) {
      console.error('Error sending bulk invoices:', error);
      alert('Error sending bulk invoices');
    } finally {
      setIsSendingAll(false);
    }
  };
  
  const fetchInvoices = async () => {
    try {
      setLoading(true);
      // Build query parameters based on current filter state
      const params = {
        period: filterPeriod
      };
      
      if (filterPeriod === 'month') {
        params.month = selectedMonth.month;
        params.year = selectedMonth.year;
      }
      
      const response = await axios.get(`${API}/invoices`, { params });
      setInvoices(response.data);
      fetchInvoiceSummary(); // Fetch summary when invoices are loaded
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceSummary = async () => {
    try {
      const response = await axios.get(`${API}/invoices/summary`, {
        params: {
          start_date: exportStartDate,
          end_date: exportEndDate
        }
      });
      setInvoiceSummary(response.data);
    } catch (error) {
      console.error('Error fetching invoice summary:', error);
    }
  };

  const handleDeleteInvoice = async (invoiceId, invoiceNumber) => {
    if (window.confirm(`Are you sure you want to delete invoice ${invoiceNumber}? This action cannot be undone.`)) {
      try {
        await axios.delete(`${API}/invoices/${invoiceId}`);
        await fetchInvoices(); // Refresh the list
        alert('Invoice deleted successfully!');
      } catch (error) {
        console.error('Error deleting invoice:', error);
        alert('Error deleting invoice: ' + (error.response?.data?.detail || error.message));
      }
    }
  };
  
  const getStatusBadge = (status) => {
    const variants = {
      draft: 'secondary',
      sent: 'default',
      overdue: 'destructive',
      paid: 'success'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  const handleExcelExport = async () => {
    if (!exportStartDate || !exportEndDate) {
      alert('Please select both start and end dates for export');
      return;
    }

    if (new Date(exportStartDate) > new Date(exportEndDate)) {
      alert('Start date cannot be after end date');
      return;
    }

    setIsExporting(true);
    
    try {
      const response = await axios.get(`${API}/invoices/export-excel`, {
        params: {
          start_date: exportStartDate,
          end_date: exportEndDate
        },
        responseType: 'blob'
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoices_${exportStartDate}_to_${exportEndDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('Excel export completed successfully!');
    } catch (error) {
      console.error('Export error:', error);
      const message = error.response?.data?.detail || error.message || 'Export failed';
      alert(`Export failed: ${message}`);
    } finally {
      setIsExporting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50">
      {/* Header Section - Mobile Responsive */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-orange-100/50 px-4 sm:px-6 py-6">
        <div className="max-w-7xl mx-auto">
          {/* Title Section */}
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
              Invoice Management
            </h1>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">Generate and manage invoices with GST compliance and automated billing</p>
          </div>
          
          {/* Filter Section */}
          <div className="bg-white p-4 rounded-xl shadow-lg border border-gray-200 mb-4">
            <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:items-center">
                <label className="text-sm font-semibold text-gray-700">Filter Period:</label>
                <select
                  value={filterPeriod}
                  onChange={(e) => setFilterPeriod(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white text-sm"
                >
                  <option value="overall">Overall</option>
                  <option value="month">Month</option>
                </select>
                
                {filterPeriod === 'month' && (
                  <div className="flex gap-2">
                    <select
                      value={selectedMonth.month}
                      onChange={(e) => setSelectedMonth({ ...selectedMonth, month: parseInt(e.target.value) })}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white text-sm"
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(2025, i, 1).toLocaleString('default', { month: 'long' })}
                        </option>
                      ))}
                    </select>
                    <select
                      value={selectedMonth.year}
                      onChange={(e) => setSelectedMonth({ ...selectedMonth, year: parseInt(e.target.value) })}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white text-sm"
                    >
                      {Array.from({ length: 5 }, (_, i) => (
                        <option key={2024 + i} value={2024 + i}>
                          {2024 + i}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              
              {dashboardContext.period && (
                <div className="text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
                  Filtered from Dashboard: {dashboardContext.period === 'month' ? 
                    `${new Date(2025, dashboardContext.month - 1, 1).toLocaleString('default', { month: 'long' })} ${dashboardContext.year}` : 
                    'Overall'}
                </div>
              )}
            </div>
          </div>
          
          {/* Cards and Export Section - Mobile Stacked */}
          <div className="space-y-4">
            {/* Total Invoices Card - Full Width on Mobile */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-3 rounded-xl shadow-lg">
              <div className="text-center sm:text-left">
                <div className="text-xs font-medium opacity-90 mb-1">Total Invoices</div>
                <div className="text-2xl font-bold">{invoices.length}</div>
              </div>
            </div>
            
            {/* Excel Export Section - Mobile Optimized */}
            <div className="bg-white p-4 rounded-xl shadow-lg border border-gray-200">
              <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider block">Export Period</label>
                
                {/* Mobile: Stack dates vertically, Desktop: Side by side */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
                  <Input
                    type="date"
                    value={exportStartDate}
                    onChange={(e) => {
                      setExportStartDate(e.target.value);
                      setTimeout(() => fetchInvoiceSummary(), 100);
                    }}
                    className="w-full sm:w-32 text-sm border-2 border-gray-200 focus:border-blue-400"
                    max={exportEndDate}
                  />
                  <span className="text-gray-400 font-medium text-xs sm:text-sm self-center">to</span>
                  <Input
                    type="date"
                    value={exportEndDate}
                    onChange={(e) => {
                      setExportEndDate(e.target.value);
                      setTimeout(() => fetchInvoiceSummary(), 100);
                    }}
                    className="w-full sm:w-32 text-sm border-2 border-gray-200 focus:border-blue-400"
                    min={exportStartDate}
                  />
                  
                  {/* Export Button */}
                  <Button
                    onClick={handleExcelExport}
                    disabled={isExporting}
                    className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold px-4 py-2 rounded-lg shadow-lg mt-2 sm:mt-0"
                  >
                    {isExporting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Export
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Summary Totals Section */}
                {invoiceSummary && (
                  <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Selected Period Totals</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Total Amount</div>
                        <div className="text-lg font-bold text-gray-900">₹{invoiceSummary.total_amount?.toLocaleString() || '0'}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Total Tax</div>
                        <div className="text-lg font-bold text-blue-600">₹{invoiceSummary.total_tax?.toLocaleString() || '0'}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Grand Total</div>
                        <div className="text-lg font-bold text-green-600">₹{invoiceSummary.grand_total?.toLocaleString() || '0'}</div>
                      </div>
                    </div>
                    <div className="text-center mt-2">
                      <div className="text-xs text-gray-500">Invoices: {invoiceSummary.invoice_count || 0}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons Section - Mobile Responsive */}
      <div className="bg-gray-50/50 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Button
              onClick={() => setShowCreateInvoice(true)}
              className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white font-semibold rounded-lg shadow-lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
            
            <Button
              onClick={sendAllUnpaidInvoices}
              disabled={isSendingAll}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold rounded-lg shadow-lg"
            >
              {isSendingAll ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send All Due
                </>
              )}
            </Button>
            
            <Button
              onClick={() => setShowGenerateInvoices(true)}
              className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold rounded-lg shadow-lg sm:col-span-2 lg:col-span-1"
            >
              <Receipt className="w-4 h-4 mr-2" />
              Generate Invoices
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
        {/* Statistics Cards - Mobile Responsive */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-xl">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold mb-2">{invoices.filter(i => i.status === 'paid').length}</div>
              <div className="text-green-100">Paid Invoices</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-500 to-orange-600 text-white border-0 shadow-xl">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold mb-2">{invoices.filter(i => i.status === 'sent').length}</div>
              <div className="text-yellow-100">Pending Invoices</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-500 to-pink-600 text-white border-0 shadow-xl">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold mb-2">{invoices.filter(i => i.status === 'overdue').length}</div>
              <div className="text-red-100">Overdue Invoices</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white border-0 shadow-xl">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold mb-2">₹{invoices.reduce((sum, i) => sum + (i.total_amount || 0), 0).toLocaleString()}</div>
              <div className="text-purple-100">Total Revenue</div>
            </CardContent>
          </Card>
        </div>

        {/* Invoices Table - Mobile Responsive */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
          <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-2xl font-bold flex items-center">
              <IndianRupee className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" />
              Invoice Records
            </CardTitle>
            <CardDescription className="text-indigo-100 text-sm">
              Track all your invoices, payments, and billing status
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 space-y-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-12 bg-gray-200 rounded w-32 animate-pulse"></div>
                    <div className="h-12 bg-gray-200 rounded flex-1 animate-pulse"></div>
                    <div className="h-12 bg-gray-200 rounded w-24 animate-pulse"></div>
                    <div className="h-12 bg-gray-200 rounded w-32 animate-pulse"></div>
                    <div className="h-12 bg-gray-200 rounded w-40 animate-pulse"></div>
                  </div>
                ))}
              </div>
            ) : invoices.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
                <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mx-auto mb-8">
                  <FileText className="w-16 h-16 text-purple-600" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">No Invoices Found</h3>
                <p className="text-lg text-gray-600 max-w-md mb-8 leading-relaxed">
                  Create professional GST-compliant invoices in seconds. Add items, taxes, and send to your clients instantly.
                </p>
                <Button 
                  onClick={() => setShowCreateInvoice(true)}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold px-10 py-6 text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 h-14"
                >
                  <Plus className="w-6 h-6 mr-2" />
                  Create First Invoice
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-orange-100">
                      <TableHead className="font-semibold text-gray-700">Invoice #</TableHead>
                      <TableHead className="font-semibold text-gray-700">Client</TableHead>
                      <TableHead className="font-semibold text-gray-700">Service</TableHead>
                      <TableHead className="font-semibold text-gray-700">Amount</TableHead>
                      <TableHead className="font-semibold text-gray-700">GST</TableHead>
                      <TableHead className="font-semibold text-gray-700">Total</TableHead>
                      <TableHead className="font-semibold text-gray-700">Invoice Date</TableHead>
                      <TableHead className="font-semibold text-gray-700">Status</TableHead>
                      <TableHead className="font-semibold text-gray-700">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>{invoice.invoice_number}</TableCell>
                  <TableCell>{invoice.client?.name || invoice.client_name || 'N/A'}</TableCell>
                  <TableCell>{invoice.service?.name || invoice.service_name || 'Ad-hoc'}</TableCell>
                  <TableCell>₹{(invoice.amount || 0).toLocaleString()}</TableCell>
                  <TableCell>₹{(invoice.gst_amount || 0).toLocaleString()}</TableCell>
                  <TableCell>₹{(invoice.total_amount || 0).toLocaleString()}</TableCell>
                  <TableCell>{new Date(invoice.invoice_date).toLocaleDateString()}</TableCell>
                  <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                  <TableCell>
                    <div className="flex space-x-1">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setShowViewModal(true);
                        }}
                        data-testid={`view-invoice-${invoice.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={async () => {
                          try {
                            const response = await axios.get(`${API}/invoices/${invoice.id}/pdf`, {
                              responseType: 'blob'
                            });
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `Invoice_${invoice.invoice_number}.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                          } catch (error) {
                            console.error('Error downloading PDF:', error);
                            alert('Error downloading PDF: ' + (error.response?.data?.detail || error.message));
                          }
                        }}
                        className="text-orange-600 hover:text-orange-700"
                        data-testid={`download-pdf-${invoice.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setShowEditModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-700"
                        data-testid={`edit-invoice-${invoice.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleDeleteInvoice(invoice.id, invoice.invoice_number)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={async () => {
                          const invoiceText = `Invoice #: ${invoice.invoice_number}
Due: ${new Date(invoice.due_date).toLocaleDateString()}
${invoice.client?.name || invoice.client_name || 'Client'}
Amount: Rs.${invoice.total_amount}

Pay now to avoid late fees.

GSTAssist - Professional GST Management`;

                          try {
                            const clientMobile = invoice.client?.mobile || invoice.client_mobile || '';
                            if (!clientMobile) {
                              alert('No mobile number available for this client');
                              return;
                            }
                            if (window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                              // Mobile - use WhatsApp URL
                              const whatsappUrl = `https://wa.me/${clientMobile.replace(/[^\d]/g, '')}?text=${encodeURIComponent(invoiceText)}`;
                              window.open(whatsappUrl, '_blank');
                            } else {
                              // Desktop - use our integration
                              const response = await axios.post(`${API}/whatsapp/web/send`, {
                                phone_number: clientMobile,
                                message: invoiceText
                              });
                              
                              if (response.data.success) {
                                alert('Invoice sent via WhatsApp!');
                              } else {
                                throw new Error('Send failed');
                              }
                            }
                          } catch (error) {
                            console.error('Error sending invoice:', error);
                            alert('Error sending invoice');
                          }
                        }}
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View Invoice Modal */}
      {showViewModal && selectedInvoice && (
        <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <ViewInvoice 
              invoice={selectedInvoice} 
              onClose={() => setShowViewModal(false)} 
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Invoice Modal */}
      {showEditModal && selectedInvoice && (
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <EditInvoice 
              invoice={selectedInvoice} 
              onClose={() => setShowEditModal(false)}
              onSave={() => {
                setShowEditModal(false);
                fetchInvoices();
              }} 
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Generate Invoices Modal */}
      {showGenerateInvoices && (
        <Dialog open={showGenerateInvoices} onOpenChange={setShowGenerateInvoices}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Generate Invoices</DialogTitle>
              <DialogDescription>
                Generate invoices for all active clients based on their billing frequency.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-600">
                This will generate invoices for all clients who have active services and are due for billing based on their service frequency (monthly, quarterly, yearly).
              </p>
              
              {/* Backfill Option */}
              <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <input
                  type="checkbox"
                  id="backfill-option"
                  checked={backfillMissing}
                  onChange={(e) => setBackfillMissing(e.target.checked)}
                  className="mt-1 h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                />
                <div className="flex-1">
                  <label htmlFor="backfill-option" className="text-sm font-medium text-gray-900 cursor-pointer">
                    Generate invoices for missing previous periods
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    If checked, will generate all missing invoices from the service start date. If unchecked, will only generate for the current period.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={async () => {
                    if (isGeneratingInvoices) return;  // Prevent double-click
                    
                    try {
                      setIsGeneratingInvoices(true);
                      const response = await axios.post(`${API}/generate-invoices`, {
                        backfill: backfillMissing
                      });
                      alert(response.data.message);
                      fetchInvoices();
                      setShowGenerateInvoices(false);
                      setBackfillMissing(false);  // Reset for next time
                    } catch (error) {
                      console.error('Error generating invoices:', error);
                      alert('Error generating invoices');
                    } finally {
                      setIsGeneratingInvoices(false);
                    }
                  }}
                  className="flex-1"
                  disabled={isGeneratingInvoices}
                >
                  {isGeneratingInvoices ? 'Generating...' : 'Generate Invoices'}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => setShowGenerateInvoices(false)}
                  className="flex-1"
                  disabled={isGeneratingInvoices}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Invoices;
